import { WHATSAPP } from '../../../constants/whatsapp';
import {
  Component,
  OnInit,
  inject,
  signal,
  computed,
  OnDestroy,
  PLATFORM_ID,
  Input,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonModule, isPlatformServer } from '@angular/common';
import { ProductList } from '../product-list/product-list';
import { CategoriesService } from '../../../services/categories.service';
import type { Producto } from '../../../types/product.types';
import type { Category } from '../../../types/category.types';
import { ProductService } from '../../../services/product.service';
import { CartService } from '../../../services/cart.service';
import { NotificationService } from '../../../services/notification.service';
import { SeoService } from '../../../services/seo.service';
import { SkeletonModule } from 'primeng/skeleton';
import { VehicleService } from '../../../services/vehicle.service';
import { Subscription } from 'rxjs';
import { Auto } from '../../../types/vehicle.types';
import { smartTitle } from '../../../utils/pipes/smart-capitalize';
import { fromSlug } from '../../../utils/slugs/slug.utils';

@Component({
  selector: 'allas-products-page',

  imports: [CommonModule, ProductList, SkeletonModule],
  templateUrl: 'products-page.component.html',
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
})
export class ProductsPage implements OnInit, OnDestroy {
  readonly whatsapp = WHATSAPP;
  @Input() h1DescriptionTitle: string = '';

  private route = inject(ActivatedRoute);
  private categoriesService = inject(CategoriesService);
  private seoService = inject(SeoService);

  productService = inject(ProductService);
  cartService = inject(CartService);
  notificationService = inject(NotificationService);
  vehicleService = inject(VehicleService);
  vehiclesSubscription: Subscription | undefined;
  private debounceTimer: any = null;

  // Detectar si estamos en prerender
  isPrerender = isPlatformServer(inject(PLATFORM_ID));

  // Array de cuadros de skeletons
  skeletons = Array.from({ length: 10 }, (_, index) => index);

  // State
  loading = signal<boolean>(true);
  productsFetchedByVehicle = signal<boolean>(false);
  defaultVehicleAlias = signal<string>('');
  products = signal<Producto[]>([]);
  currentCategory = signal<Category | null>(null);

  // Estado para el botón de agregar al carrito
  addingToCart = signal(false);

  // Computed values
  categoryName = computed(() => {
    const category = this.currentCategory();
    return category?.name || '';
  });

  marca = signal<string | null>(null);

  vehiculoSeleccionado = signal<Auto | null>(null);
  marcaVehiculoFromSlug = signal<string>('');
  modeloVehiculoFromSlug = signal<string>('');
  anioVehiculoFromSlug = signal<string>('');
  marcaVehiculo = computed(() =>
    smartTitle(this.marcaVehiculoFromSlug() || '')
  );
  modeloVehiculo = computed(() =>
    smartTitle(this.modeloVehiculoFromSlug() || '')
  );
  anioVehiculo = computed(() => this.anioVehiculoFromSlug());

  ngOnInit() {
    // this.setMetaTags();

    // Solo hacer peticiones si NO estamos en prerender
    if (!this.isPrerender) {
      this.setSlug();
      this.subscribeToVehicles();
    } else {
      // En prerender, solo establecer datos básicos sin peticiones
      this.setBasicDataForPrerender();
    }
  }

  ngOnDestroy() {
    if (this.vehiclesSubscription) {
      this.vehiclesSubscription.unsubscribe();
    }

    // Limpiar el timer del debounce
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }

  /**
   * Establece datos básicos para prerender sin hacer peticiones
   */
  private setBasicDataForPrerender() {
    this.route.params.subscribe((params) => {
      const slug = params['slug'];

      const marcaVehiculo = fromSlug(params['marca'] || '');
      const modeloVehiculo = fromSlug(params['modelo'] || '');
      const anioVehiculo = params['anio'];

      const marcaProducto = params['marcaProducto'];
      if (marcaProducto) {
        this.marca.set(marcaProducto);
      }

      const category = this.categoriesService.findCategoryBySlug(slug);

      this.marcaVehiculoFromSlug.set(marcaVehiculo);
      this.modeloVehiculoFromSlug.set(modeloVehiculo);
      this.anioVehiculoFromSlug.set(anioVehiculo);

      if (category) {
        this.currentCategory.set(category);
      }

      // this.setMetaTags();
    });
  }

  private setMetaTags() {
    return;
    const categoryName = this.categoryName();
    const marca = this.marca();
    const vehiculo = smartTitle(
      `${this.marcaVehiculo() || ''} ${this.modeloVehiculo() || ''} ${
        this.anioVehiculo() || ''
      }` || ''
    ).trim();

    // Usar el servicio SEO para configurar todos los meta tags y schema LD
    this.seoService.setProductsPageMetaTags(
      categoryName || 'Repuestos para carro',
      marca || undefined,
      this.products(),
      vehiculo
    );
  }

  private subscribeToVehicles(): void {
    this.vehiculoSeleccionado.set(
      this.vehicleService.getDefaultVehicle() || null
    );

    this.vehiclesSubscription = this.vehicleService.vehicles$.subscribe(
      (data) => {
        const nuevoVehiculoSeleccionado = data.find(
          (vehicle) => vehicle.predeterminado
        );

        if (nuevoVehiculoSeleccionado?.id !== this.vehiculoSeleccionado()?.id) {
          this.setSlug(Boolean(nuevoVehiculoSeleccionado));
        }

        this.vehiculoSeleccionado.set(nuevoVehiculoSeleccionado || null);
      }
    );
  }

  setSlug(fetchByVehicle: boolean = false) {
    this.route.params.subscribe((params) => {
      const slug = params['slug'];

      const marcaVehiculo = params['marca'];
      const modeloVehiculo = params['modelo'];
      const anioVehiculo = params['anio'];

      const marcaProducto = params['marcaProducto'];
      if (marcaProducto) {
        console.log('FETCHING PRODUCTS BY BRAND:', marcaProducto);
        this.marca.set(marcaProducto);
        this.loadProductsByBrand(marcaProducto);
        return;
      }

      this.marcaVehiculoFromSlug.set(marcaVehiculo);
      this.modeloVehiculoFromSlug.set(modeloVehiculo);
      this.anioVehiculoFromSlug.set(anioVehiculo);

      // Si hay slug, buscar categoría y cargar productos de esa categoría
      if (slug) {
        const category = this.categoriesService.findCategoryBySlug(slug);
        if (category) {
          this.loadProductsByCategory(category.slug, fetchByVehicle);
        } else {
          // Si no se encuentra la categoría, cargar todos los productos
          this.loadAllProducts();
        }
      } else {
        // Si no hay slug, cargar todos los productos
        this.loadAllProducts();
      }
    });
  }

  private loadProductsByCategory(
    slug: string,
    fetchByVehicle: boolean = false
  ) {
    // Limpiar el timer anterior si existe
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // Establecer nuevo timer con debounce de 1.5 segundos
    this.debounceTimer = setTimeout(() => {
      const vehiculoSeleccionado = this.vehicleService.getDefaultVehicle();
      this.productsFetchedByVehicle.set(fetchByVehicle);
      this.defaultVehicleAlias.set(vehiculoSeleccionado?.alias || '');

      // Load category info
      const category = this.categoriesService.findCategoryBySlug(slug);
      this.currentCategory.set(category);
      this.loading.set(true);
      this.productsFetchedByVehicle.set(fetchByVehicle);

      // Load products from API
      this.productService.getProductsByCategory(slug).subscribe({
        next: (response) => {
          if (response) {
            this.products.set(response);
          } else {
            this.products.set([]);
          }
          this.loading.set(false);
          // Actualizar meta tags y schema LD después de cargar productos
          // this.setMetaTags();
        },
        error: (error) => {
          console.error('❌ Error cargando productos:', error);
          this.products.set([]);
          this.loading.set(false);
          // Actualizar meta tags y schema LD incluso si hay error
          // this.setMetaTags();
        },
        complete: () => {
          this.loading.set(false);
        },
      });
    }, 500);
  }

  private loadProductsByBrand(marca: string) {
    this.loading.set(true);

    this.productService.getAllProducts({ marca, limit: 200 }).subscribe({
      next: (response) => {
        const productsSortedByImages = response.sort((a, b) => {
          return (b.imagenes.xl.length || 0) - (a.imagenes.xl.length || 0);
        });
        this.products.set(productsSortedByImages);
        this.loading.set(false);
        // Actualizar meta tags y schema LD después de cargar productos
        // this.setMetaTags();
      },
      error: (error) => {
        console.error('❌ Error cargando productos:', error);
        this.products.set([]);
        this.loading.set(false);
        // Actualizar meta tags y schema LD incluso si hay error
        // this.setMetaTags();
      },
    });
  }

  private loadAllProducts() {
    // Limpiar el timer anterior si existe
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // Establecer nuevo timer con debounce de 500ms
    this.debounceTimer = setTimeout(() => {
      const vehiculoSeleccionado = this.vehicleService.getDefaultVehicle();
      this.productsFetchedByVehicle.set(false);
      this.defaultVehicleAlias.set(vehiculoSeleccionado?.alias || '');
      this.loading.set(true);

      // Load all products from API
      this.productService.getAllProducts({ limit: 200 }).subscribe({
        next: (response) => {
          if (response && response.length > 0) {
            // Ordenar productos por cantidad de imágenes (productos con más imágenes primero)
            const productsSortedByImages = response.sort((a, b) => {
              return (b.imagenes.xl.length || 0) - (a.imagenes.xl.length || 0);
            });
            this.products.set(productsSortedByImages);
          } else {
            this.products.set([]);
          }
          this.loading.set(false);
        },
        error: (error) => {
          console.error('❌ Error cargando productos:', error);
          this.products.set([]);
          this.loading.set(false);
        },
        complete: () => {
          this.loading.set(false);
        },
      });
    }, 500);
  }

  /**
   * Agrega la categoría actual al carrito
   */
  addToCart(): void {
    const category = this.currentCategory();
    if (!category) return;

    this.addingToCart.set(true);

    // Simular un pequeño delay para mostrar el estado de loading
    setTimeout(() => {
      this.cartService.addItemToCart({
        categoria: category,
        cantidad: 1,
      });
      this.addingToCart.set(false);

      // Mostrar notificación de éxito
      this.showSuccessNotification();
    }, 800);
  }

  /**
   * Incrementa la cantidad en el carrito
   */
  increaseCartQuantity(): void {
    const category = this.currentCategory();
    if (!category) return;

    this.cartService.addItemToCart({
      categoria: category,
      cantidad: 1,
    });
    this.showSuccessNotification();
  }

  /**
   * Muestra una notificación de éxito temporal
   */
  private showSuccessNotification(): void {
    // Aquí podrías integrar un servicio de notificaciones
    // Por ahora usamos un alert simple
    const categoryName = this.currentCategory()?.name || '';
    this.notificationService.showSuccess(
      `${categoryName} agregado al carrito de cotización!`
    );
  }
}
